import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/ArrowUpward';
import { AgentMarketPlaceItemWithFilter } from '../agentMarketPlace/AgentMarketPlace';
import { useMsal } from '@azure/msal-react';
import { askAI, askPlatformTeams, getEnvironmentFromUrl } from '../../../data/api/api';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Divider } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { useIngestImagesMutation, useGetDatasetQuery } from '../../../data/api/endPointsSlice';
import ChatBanner from '../../../shared/chatBanner/ChatBanner';
import MarkdownRenderer from '../../../shared/markdownRenderer/MarkdownRenderer';
import CopyToClipboard from '../../../shared/copyToClipBoard/CopyToClipboard';
import { LogButtonEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import {
  askButtonClickedText,
  changeAgentButtonClickedText,
  fileUploadedText,
} from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';

interface ChatProps {
  setShowChat: (showChat: boolean) => void;
  agentDetails: AgentMarketPlaceItemWithFilter;
  isUploadRequired: boolean;
  file_format_supported: string[];
}

export interface IngestImagesParams {
  partyId: string;
  files: File[];
}

export interface DownloadAgentParams {
  partyId: string;
  agentName: string;
  custom_ui?: string;
  download_url?: string;
}

const PromtChat = ({ setShowChat, agentDetails, isUploadRequired, file_format_supported }: ChatProps) => {
  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();
  const fullName = accounts[0].name;
  const initials = (fullName ?? '')
    .split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase();

  const [message, setMessage] = useState<any>('');
  const [agentResponse, setAgentResponse] = useState<
    [
      {
        source: string;
        content: string;
      },
    ]
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [userQuery, setUserQuery] = useState<string>('');

  const [fileName, setFileName] = useState<string[]>([]); // State to store the uploaded file name
  const [showTooltip, setTooltip] = useState(false);

  const uuId = useRef<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const markdownContentRef = useRef<HTMLDivElement | null>(null);
  const isTeamResponsePending = useRef(false);
  const conversationIDRef = useRef<any>(null);
  const tooltipTextRef = useRef<HTMLButtonElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ingestImages] = useIngestImagesMutation();
  const [formattedContentElement, setFormattedContentElement] = useState<HTMLElement | null>(null);

  const environment = getEnvironmentFromUrl();
  const datasetId =
    agentDetails.supports_dataset !== null && agentDetails.supports_dataset === 'existing' && agentDetails.datasetIds
      ? agentDetails.datasetIds[environment]
      : undefined;

  const { data: DatasetContent, isLoading: datasetContentLoading } = useGetDatasetQuery(datasetId!, {
    skip: !datasetId,
  });

  useEffect(() => {
    const textElement = tooltipTextRef.current;
    if (textElement) {
      setTooltip(textElement.scrollHeight > textElement.clientHeight);
    }
  }, []);

  const handleSend = async () => {
    if (message.trim()) {
      LogButtonEventToGA(askButtonClickedText);
      // Add user's message to the chat
      const currentMessage = message;
      setUserQuery(currentMessage);
      setMessage('');
      // Clear previous response when sending a new message
      setAgentResponse(undefined);

      // Call the API
      if (agentDetails.isPlatformAgent) {
        await askPlatformTeams(
          currentMessage,
          agentDetails,
          setAgentResponse,
          setIsLoading,
          isTeamResponsePending,
          conversationIDRef,
          initials,
          fileName
        );
      } else {
        if (uuId.current === null) {
          uuId.current = crypto.randomUUID();
        }
        if (isUploadRequired && fileName.length === 0) {
          enqueueSnackbar(`Please upload a file before sending the message.`, {
            variant: 'error',
          });
          return;
        }
        await askAI(currentMessage, agentDetails, setAgentResponse, setIsLoading, uuId.current, fileName);
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    //Early exit if no files selected (e.g., file dialog canceled)
    if (!files || files.length === 0) return;

    try {
      const uploadSnackbarKey = enqueueSnackbar('File upload in progress', { variant: 'success', persist: true });
      const fileArray = Array.from(files);
      const invalidFileNameRegex = /[^\w.-]/;

      // Filter files with invalid names
      const invalidFiles = fileArray.filter((file) => invalidFileNameRegex.test(file.name));

      if (invalidFiles.length > 0) {
        enqueueSnackbar(
          `The following files have invalid names (spaces or special characters) and cannot be uploaded:\n${invalidFiles
            .map((file) => file.name)
            .join('\n')}`,
          {
            variant: 'error',
          }
        );
        return; // Stop further execution if invalid files are found
      }

      // Check if file extensions are supported
      const unsupportedFiles = fileArray.filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        return !file_format_supported.includes(`.${extension}`);
      });

      if (unsupportedFiles.length > 0) {
        closeSnackbar(uploadSnackbarKey);
        enqueueSnackbar(
          `Unsupported file format(s): ${unsupportedFiles
            .map((file) => file.name)
            .join(', ')}\nOnly ${file_format_supported.join(', ')} formats are supported.`,
          {
            variant: 'error',
          }
        );
        return; // Stop further execution if unsupported files are found
      }

      // Update the file names in the state
      setFileName(Array.from(files).map((file) => file.name));

      // Call the ingestImages API
      const response = await ingestImages({
        partyId: agentDetails._id,
        files: fileArray,
      }).unwrap();

      if (response) {
        setFileName(fileArray.map((file) => file.name));
        LogButtonEventToGA(fileUploadedText);
        closeSnackbar(uploadSnackbarKey);
        enqueueSnackbar('File uploaded successfully!', { variant: 'success' });
      } else {
        closeSnackbar(uploadSnackbarKey);
        console.error('File upload failed.');
        enqueueSnackbar('Upload failed. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      closeSnackbar();
      setFileName([]);
      enqueueSnackbar('An unexpected error occurred during file upload.', {
        variant: 'error',
      });
      console.error('File upload error:', error);
    }
  };

  const changeAgent = () => {
    LogButtonEventToGA(changeAgentButtonClickedText);
    localStorage.setItem('scrollToWhySection', 'true');
    setShowChat(false);
    uuId.current = null;
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    if (agentResponse && markdownContentRef.current) {
      setFormattedContentElement(markdownContentRef.current);
    }
  }, [agentResponse]);

  return (
    <>
      <ChatBanner changeAgent={changeAgent} agentDetails={agentDetails} />
      <Box sx={{ height: isMobile ? 'auto' : 'calc(100vh - 250px)', overflow: 'hidden' }}>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          p={isMobile ? 1 : 2}
          gap={isMobile ? 1 : 2}
          height="100%"
        >
          {/* Left Panel */}
          <Box
            sx={{
              width: isMobile ? '100%' : 500,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              position: isMobile ? 'relative' : 'sticky',
              top: 0,
            }}
          >
            <Paper elevation={0}>
              {/* Content at the top */}
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                Test Prompts
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Scrollable prompt section */}
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  mt: 3,
                  minHeight: '400px',
                }}
              >
                {[agentDetails.prompt1, agentDetails.prompt2, agentDetails.prompt3].map((promptKey, index) => {
                  return (
                    <Button
                      ref={tooltipTextRef}
                      key={`${agentDetails._id}-${index}`}
                      variant="outlined"
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        width: '100%',
                        padding: 1,
                        borderRadius: 2,
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        height: 'auto',
                        WebkitLineClamp: 3,
                        lineHeight: '27px',
                      }}
                      onClick={() => {
                        const promptText = promptKey;
                        LogButtonEventToGA(`Test Prompt ${index + 1} Clicked`);
                        setMessage(''); // Clear the input field immediately
                        setUserQuery(promptText);
                        setAgentResponse(undefined);
                        // Use the promptText directly in handleSend to ensure it's used immediately
                        if (agentDetails.isPlatformAgent) {
                          askPlatformTeams(
                            promptText,
                            agentDetails,
                            setAgentResponse,
                            setIsLoading,
                            isTeamResponsePending,
                            conversationIDRef,
                            initials,
                            fileName
                          );
                        } else {
                          if (uuId.current === null) {
                            uuId.current = crypto.randomUUID();
                          }
                          askAI(promptText, agentDetails, setAgentResponse, setIsLoading, uuId.current, fileName);
                        }
                      }}
                    >
                      {showTooltip ? (
                        <Tooltip title={promptKey} arrow placement="top" sx={{ lineHeight: '27px' }}>
                          <span>{promptKey}</span>
                        </Tooltip>
                      ) : (
                        <span>{promptKey}</span>
                      )}
                    </Button>
                  );
                })}
              </Box>

              {/* Input area fixed at the bottom */}
              <Divider sx={{ my: 2 }} />
              {agentDetails.supports_dataset !== null && !datasetContentLoading && DatasetContent?.name && (
                <Chip
                  label={`Dataset Name : ${DatasetContent.name}`}
                  color="primary"
                  sx={{ maxWidth: 'fit-content', marginBottom: '5px' }}
                />
              )}

              {fileName.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: '10px' }}>
                  {fileName.map((file, index) => (
                    <Chip
                      key={index}
                      label={file}
                      color="primary"
                      onDelete={() => {
                        const newFiles = [...fileName];
                        newFiles.splice(index, 1);
                        setFileName(newFiles);
                      }}
                      sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    />
                  ))}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: 2,
                  mt: 'auto',
                }}
              >
                <TextField
                  variant="outlined"
                  size="medium"
                  placeholder={isUploadRequired ? 'Upload file and provide a query' : 'Please provide a query'}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  sx={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    width: '100%',
                    marginBottom: isMobile ? '10px' : '0',
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiInputBase-input': {
                        minWidth: '230px',
                      },
                    },
                    endAdornment: isUploadRequired && (
                      <InputAdornment position="end">
                        <input
                          accept={file_format_supported.join(',')}
                          style={{ display: 'none', padding: 0 }}
                          id="icon-button-file"
                          type="file"
                          onChange={handleFileUpload}
                          multiple
                        />
                        <Tooltip
                          title={`Upload File (Supported: ${file_format_supported.join(', ')})`}
                          arrow
                          placement="top"
                        >
                          <label htmlFor="icon-button-file">
                            <IconButton color="primary" component="span">
                              <UploadFileIcon />
                            </IconButton>
                          </label>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={message === '' && fileName.length === 0}
                  endIcon={<SendIcon />}
                  sx={{
                    height: '46px',
                    px: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  Ask
                </Button>
              </Box>
            </Paper>
          </Box>

          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem
            sx={{ mx: isMobile ? 0 : 1, my: isMobile ? 1 : 0 }}
          />

          {/* Right Panel */}
          <Box
            sx={{
              width: isMobile ? '100%' : '90%',
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                height: '3rem',
                position: isMobile ? 'relative' : 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  marginTop: 2,
                  fontSize: '1rem',
                }}
              >
                Agent Response
              </Typography>
              {/* <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #0d47a1',
                  borderRadius: 2,
                  p: 1,
                  width: 'fit-content',
                }}
              >
                <IconButton size="small">
                  <ThumbUpAltOutlinedIcon fontSize="small" sx={{ color: '#0d47a1' }} />
                </IconButton>
                <IconButton size="small">
                  <ThumbDownAltOutlinedIcon fontSize="small" sx={{ color: '#0d47a1' }} />
                </IconButton>
                <IconButton size="small">
                  <MoreHorizIcon fontSize="small" sx={{ color: '#0d47a1' }} />
                </IconButton>
              </Paper> */}
            </Box>
            <Divider sx={{ margin: '7px 0 16px' }} />
            {/* Chat Container */}
            <Box ref={chatContainerRef} flex={1} px={2} py={3}>
              <Box
                sx={{
                  padding: '12px 16px',
                  maxWidth: '100%',
                  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  color: 'rgb(51, 51, 51)',
                  letterSpacing: '0.00938em',
                }}
              >
                <Box sx={{ marginBottom: '1rem' }}>
                  {userQuery && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        padding: '12px',
                        backgroundColor: '#f5f7fb',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #e0e0e0',
                        width: '100%',
                        maxHeight: '350px',
                        overflowY: 'auto',
                      }}
                    >
                      <AccountCircleIcon sx={{ color: '#0d47a1', fontSize: '40px', mt: 0.5 }} />
                      <Typography
                        variant="body1"
                        fontWeight="500"
                        sx={{
                          whiteSpace: 'pre-wrap', // Allows wrapping and preserves line breaks
                          wordBreak: 'break-word', // Breaks long words
                          flex: 1,
                        }}
                      >
                        {userQuery}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {agentResponse &&
                  agentResponse.map((response, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        padding: '16px',
                        backgroundColor: '#eaf6ff',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #0d47a1',
                        maxHeight: '400px',
                        overflowY: 'auto',
                      }}
                    >
                      <Avatar sx={{ bgcolor: '#0d47a1', width: 32, height: 32 }}>
                        <FaceRetouchingNaturalIcon sx={{ fontSize: 30 }} />
                      </Avatar>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle1" color="#1a1a1a" gutterBottom sx={{ fontWeight: 'Bold' }}>
                          {response.source}
                        </Typography>
                        <Box id={`markdownContent-${idx}`} ref={markdownContentRef}>
                          <MarkdownRenderer content={response.content} />
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <CopyToClipboard
                            textToCopy={response.content}
                            formattedContent={formattedContentElement}
                            preserveFormatting={true}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </Box>

              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PromtChat;
